import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  OnboardingProvider,
  OnboardingProgress,
  OnboardingNav,
  useOnboarding,
  useForwardAction,
} from '@/components/onboarding'
import { CredentialsStep } from '@/components/onboarding/steps/CredentialsStep'
import { AgreementsStep } from '@/components/onboarding/steps/AgreementsStep'
import { StripeConnectStep } from '@/components/onboarding/steps/StripeConnectStep'
import { PracticeProfileStep } from '@/components/onboarding/steps/PracticeProfileStep'
import { ProviderProfileStep } from '@/components/onboarding/steps/ProviderProfileStep'
import { VisitTypesStep } from '@/components/onboarding/steps/VisitTypesStep'
import { AvailabilityStep } from '@/components/onboarding/steps/AvailabilityStep'
import { ProtocolReviewStep } from '@/components/onboarding/steps/ProtocolReviewStep'
import { GoLiveStep } from '@/components/onboarding/steps/GoLiveStep'
import { useOnboardingProgress } from '@/hooks/useOnboardingProgress'
import { useProviderAuth } from '@/hooks/auth'

function AccountStep() {
  const { state, markCompleted, goToStep } = useOnboarding()

  useForwardAction({
    label: 'Continue',
    onClick: () => {
      markCompleted(state.currentStep)
      goToStep(state.currentStep + 1)
    },
  })

  return (
    <div className="space-y-3">
      <h2 className="text-2xl font-semibold text-slate-900">Account</h2>
      <p className="text-sm text-slate-600">
        Your account is created. Complete the remaining steps to set up your practice.
      </p>
    </div>
  )
}

function StepDispatcher() {
  const { state } = useOnboarding()
  switch (state.currentStep) {
    case 0:
      return <AccountStep />
    case 1:
      return <CredentialsStep />
    case 2:
      return <AgreementsStep />
    case 3:
      return <StripeConnectStep />
    case 4:
      return <PracticeProfileStep />
    case 5:
      return <ProviderProfileStep />
    case 6:
      return <VisitTypesStep />
    case 7:
      return <AvailabilityStep />
    case 8:
      return <ProtocolReviewStep />
    case 9:
      return <GoLiveStep />
    default:
      return <AccountStep />
  }
}

function WizardContent() {
  const { logout } = useProviderAuth()

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between">
            <h1 className="text-lg font-display font-semibold text-slate-900">Determa</h1>
            <button
              type="button"
              onClick={() => {
                void logout()
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <OnboardingProgress />
        <div className="mt-8">
          <StepDispatcher />
        </div>
        <OnboardingNav />
      </div>
    </div>
  )
}

export default function OnboardingWizardPage() {
  const { data: progress, isLoading, isFetching, error, refetch } = useOnboardingProgress()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p className="text-gray-600">Unable to load onboarding progress.</p>
        <Button onClick={() => void refetch()} disabled={isFetching}>
          {isFetching ? 'Retrying…' : 'Try again'}
        </Button>
      </div>
    )
  }

  return (
    <OnboardingProvider
      initialStep={progress?.current_step ?? 0}
      initialCompletedSteps={progress?.completed_steps ?? []}
      initialStepData={progress?.step_data ?? {}}
    >
      <WizardContent />
    </OnboardingProvider>
  )
}
